import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';

interface AuthState {
  token: string | null;
  employee: {
    id: string;
    firstName: string;
    lastName: string;
    role: string;
  } | null;
  restaurantId: string | null;
  isAuthenticated: boolean;
  // True when the last sign-out was the session expiring (not the user's
  // choice) — the login screen explains it calmly. Never persisted.
  sessionExpired: boolean;
  selectedRestaurantId: string | null;
  selectedRestaurantName: string | null;
  isLoadingRestaurants: boolean;

  login: (pin: string) => Promise<{ success: boolean; error?: string }>;
  logout: (expired?: boolean) => void;
  fetchRestaurantBySlug: (slugOrId: string) => Promise<boolean>;
  selectRestaurant: (id: string, name: string) => void;
  clearRestaurant: () => void;
}

// Get restaurant info from localStorage
const getStoredRestaurant = (): { id: string | null; name: string | null } => {
  const id = localStorage.getItem('mise-kds-restaurant-id');
  const name = localStorage.getItem('mise-kds-restaurant-name');
  return { id, name };
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      employee: null,
      restaurantId: null,
      isAuthenticated: false,
      sessionExpired: false,
      selectedRestaurantId: getStoredRestaurant().id,
      selectedRestaurantName: getStoredRestaurant().name,
      isLoadingRestaurants: false,

      fetchRestaurantBySlug: async (slugOrId: string) => {
        set({ isLoadingRestaurants: true });
        try {
          const apiUrl = import.meta.env.VITE_API_URL || 'https://gleaming-elegance-production-d593.up.railway.app/api';
          const response = await axios.get(`${apiUrl}/v1/restaurants/lookup/${encodeURIComponent(slugOrId)}`);

          if (response.data.success && response.data.data) {
            const { id, name } = response.data.data;
            localStorage.setItem('mise-kds-restaurant-id', id);
            localStorage.setItem('mise-kds-restaurant-name', name);
            set({ selectedRestaurantId: id, selectedRestaurantName: name, isLoadingRestaurants: false });
            return true;
          }
          set({ isLoadingRestaurants: false });
          return false;
        } catch (error) {
          console.error('Failed to lookup restaurant:', error);
          set({ isLoadingRestaurants: false });
          return false;
        }
      },

      selectRestaurant: (id: string, name: string) => {
        localStorage.setItem('mise-kds-restaurant-id', id);
        localStorage.setItem('mise-kds-restaurant-name', name);
        set({ selectedRestaurantId: id, selectedRestaurantName: name });
      },

      clearRestaurant: () => {
        localStorage.removeItem('mise-kds-restaurant-id');
        localStorage.removeItem('mise-kds-restaurant-name');
        set({ selectedRestaurantId: null, selectedRestaurantName: null });
      },

      login: async (pin: string) => {
        try {
          const restaurantId = get().selectedRestaurantId;

          if (!restaurantId) {
            return { success: false, error: 'Please select a restaurant first.' };
          }

          const apiUrl = import.meta.env.VITE_API_URL || 'https://gleaming-elegance-production-d593.up.railway.app/api';
          const response = await axios.post(`${apiUrl}/v1/auth/pin-login`, { pin, restaurantId });

          if (response.data.success) {
            const { accessToken, employee, restaurant } = response.data.data;

            // Store token for API requests
            localStorage.setItem('kds_token', accessToken);

            // Store restaurant ID for future logins
            if (restaurant?.id) {
              localStorage.setItem('mise-kds-restaurant-id', restaurant.id);
            }

            set({
              token: accessToken,
              employee: {
                id: employee.id,
                firstName: employee.firstName,
                lastName: employee.lastName,
                role: employee.role || employee.primaryRole,
              },
              restaurantId: restaurant?.id,
              isAuthenticated: true,
              sessionExpired: false,
            });

            return { success: true };
          }

          return { success: false, error: 'Login failed' };
        } catch (error: any) {
          console.error('KDS Login error:', error);
          const message = error.response?.data?.error?.message || 'Invalid PIN';
          return { success: false, error: message };
        }
      },

      logout: (expired = false) => {
        localStorage.removeItem('kds_token');
        set({
          token: null,
          employee: null,
          restaurantId: null,
          isAuthenticated: false,
          sessionExpired: expired,
        });
      },
    }),
    {
      name: 'kds-auth',
      partialize: (state) => ({
        token: state.token,
        employee: state.employee,
        restaurantId: state.restaurantId,
        isAuthenticated: state.isAuthenticated,
        selectedRestaurantId: state.selectedRestaurantId,
        selectedRestaurantName: state.selectedRestaurantName,
      }),
      onRehydrateStorage: () => (state) => {
        if (!state?.token) return;
        // A restored session whose token has expired must land on the login
        // screen once, cleanly — not render a silently-dead kitchen screen.
        try {
          const { exp } = JSON.parse(atob(state.token.split('.')[1]));
          if (exp && exp * 1000 < Date.now()) {
            localStorage.removeItem('kds_token');
            state.token = null;
            state.employee = null;
            state.restaurantId = null;
            state.isAuthenticated = false;
            state.sessionExpired = true;
            return;
          }
        } catch {
          localStorage.removeItem('kds_token');
          state.token = null;
          state.isAuthenticated = false;
          return;
        }
        // Restore token to localStorage for API requests
        localStorage.setItem('kds_token', state.token);
      },
    }
  )
);
